import React from 'react';

export default function EvaluationsModal({ isOpen, onClose, evaluations, modelName }) {
  if (!isOpen || !evaluations) return null;

  const hasError = evaluations.error !== undefined;

  // Format score for display with color coding
  const formatScore = (score) => {
    if (score === undefined || score === null) return null;

    let colorClass;
    if (score >= 0.8) colorClass = "text-green-600";
    else if (score >= 0.6) colorClass = "text-yellow-600";
    else colorClass = "text-red-600";

    return (
      <span className={`font-semibold ${colorClass}`}>
        {typeof score === 'number' ? (score * 100).toFixed(0) : score}%
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-ktx-purple text-white">
          <h3 className="text-xl font-semibold">
            Evaluation Results
            {modelName && <span className="ml-2 text-sm opacity-90">({modelName})</span>}
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-4 flex-1 overflow-auto">
          {hasError ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Evaluation Failed</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{evaluations.error}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Score Overview */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Score Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {evaluations.faithfulness_score && (
                    <div className="bg-white p-3 rounded-md border border-gray-200 shadow-sm">
                      <div className="text-sm text-gray-500 mb-1">Faithfulness</div>
                      <div className="font-medium text-lg">
                        {formatScore(evaluations.faithfulness_score.score)}
                      </div>
                    </div>
                  )}

                  {evaluations.key_point_coverage !== undefined && (
                    <div className="bg-white p-3 rounded-md border border-gray-200 shadow-sm">
                      <div className="text-sm text-gray-500 mb-1">Key Point Coverage</div>
                      <div className="font-medium text-lg">
                        {formatScore(evaluations.key_point_coverage)}
                      </div>
                    </div>
                  )}

                  {evaluations.structure_score !== undefined && (
                    <div className="bg-white p-3 rounded-md border border-gray-200 shadow-sm">
                      <div className="text-sm text-gray-500 mb-1">Structure</div>
                      <div className="font-medium text-lg">
                        {formatScore(evaluations.structure_score)}
                      </div>
                    </div>
                  )}

                  {evaluations.length_score !== undefined && (
                    <div className="bg-white p-3 rounded-md border border-gray-200 shadow-sm">
                      <div className="text-sm text-gray-500 mb-1">Length</div>
                      <div className="font-medium text-lg">
                        {formatScore(evaluations.length_score)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Faithfulness Rationale */}
              {evaluations.faithfulness_score?.rationale && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">Faithfulness Rationale</h4>
                  <p className="text-sm text-gray-700">{evaluations.faithfulness_score.rationale}</p>
                </div>
              )}

              {/* Missing Sections */}
              {evaluations.missing_sections && evaluations.missing_sections.length > 0 && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">Missing Sections</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                    {evaluations.missing_sections.map((section, idx) => (
                      <li key={idx}>{section}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Errors */}
              {evaluations.faithfulness_score?.errors && evaluations.faithfulness_score.errors.length > 0 && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">Errors Identified</h4>
                  <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                    {evaluations.faithfulness_score.errors.map((error, idx) => (
                      <li key={idx} className="text-red-600">{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-ktx-purple hover:bg-ktx-purple-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}